import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// ANT Design
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  EyeOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { message, Button, Modal, Table, Tabs, Popconfirm, Input, Form, Switch, Segmented } from 'antd';
import Tooltip from '../common/Tooltip';

import shallow from 'zustand/shallow';
import { useStore } from '../../store/store';
import { updateUrl, canUseWebEditor, canAdministrateGeneralRules } from '../../utils/browser';
import { documentFormatIconStyle, documentFormatLabel } from '../../utils/documentFormat';
import ModalImage from 'react-modal-image';
import DocumentsFolderView, { newTemplateDefaults } from './DocumentsFolderView';
import { duplicateTemplate, updateTemplateInfos } from '../../requests/templates';
import { useModalOpener, useQueryDocumentTypes } from '../../hooks';
import DocumentTypesSelector from '../common/DocumentTypesSelector';
import AdminOnlyFormItem from '../common/AdminOnlyFormItem';
import WebTemplatesPanel from './WebTemplatesPanel';
const { Search } = Input;

const selector = (state) => {
  return {
    applyTemplate: state.applyTemplate,
    deleteTemplate: state.deleteTemplate,
    currentDocument: state.currentDocument,
    currentProposal: state.currentProposal,
    proposal: state.currentProposal,
    applyTemplateAndSaveProposal: state.applyTemplateAndSaveProposal,
    startLoading: state.startLoading,
    stopLoading: state.stopLoading,
    user: state.user,
    templates: state.templates,
    sections: state.sections,
    hiddenTemplates: state.hiddenTemplates,
    hiddenSections: state.hiddenSections,
    getHiddenTemplates: state.getHiddenTemplates,
    restoreTemplate: state.restoreTemplate,
    changeCurrentTab: state.changeCurrentTab,
    updateTemplateInfosStore: state.updateTemplateInfosStore,
    duplicateTemplateStore: state.duplicateTemplateStore,
  };
};

const TemplateNameColumn = ({ record }) => {
  const { t } = useTranslation();
  return (
    <div
      type='text'
      style={{
        width: '100%',
        margin: 0,
        padding: 0,
        border: 0,
      }}
    >
      <div>{t(record.name)}</div>
    </div>
  );
};

const TemplateModal = ({
  open,
  handleOk,
  handleCancel,
  forbidClose,
  newTemplateOnClick,
  isAdmin,
}) => {
  // `shallow` : la modale reste montée (fermée) pendant toute l'édition, et
  // son sélecteur forge un objet à chaque appel — elle se redessinait, arbre
  // des documents compris, à chaque changement du store.
  const {
    applyTemplate,
    deleteTemplate,
    applyTemplateAndSaveProposal,
    startLoading,
    stopLoading,
    currentDocument,
    currentProposal,
    templates,
    sections,
    hiddenTemplates,
    hiddenSections,
    getHiddenTemplates,
    restoreTemplate,
    changeCurrentTab,
    updateTemplateInfosStore,
    duplicateTemplateStore,
    user,
  } = useStore(selector, shallow);
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(null);
  const [isRecursiveTab, setIsRecursiveTab] = useState(false);
  const [nameSearch, setNameSearch] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  // Vue affichée : 'folders' (arbre partagé) ou 'web' (templates de site web,
  // réservé à canUseWebEditor). L'ancienne vue 'list' (tables par onglets) est en
  // cours de suppression : plus aucun contrôle n'y mène, son rendu est conservé
  // plus bas (branche viewMode === 'list', inatteignable) le temps de valider la
  // vue Dossiers, et partira avec le reste de l'ancienne UI.
  const [viewMode, setViewMode] = useState('folders');
  const setEventManagerEnabled = useStore(({ setEventManagerEnabled }) => setEventManagerEnabled);

  const [isOpen, setIsOpen] = useModalOpener(false);
  const [modalSelection, setModalSelection] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState([]);
  const [form] = Form.useForm();
  const { data: documentTypes } = useQueryDocumentTypes()

  useEffect(() => {
    if (open) {
      setEventManagerEnabled(false);
    } else {
      setEventManagerEnabled(true);
    }
  }, [open, setEventManagerEnabled]);

  useEffect(() => {
    if (showHidden && isAdmin && user?.role === 'superadmin') {
      getHiddenTemplates();
    }
  }, [showHidden, isAdmin, user?.role, getHiddenTemplates]);

  const showModal = (origin, proposal) => {
    setIsOpen(true);
    setModalSelection(origin);
    setSelectedTemplate(proposal);
    if (origin === "updateTemplateInfos") {
      form.setFieldsValue({ name: proposal.name, document_type_ids: proposal.document_type_ids, admin_only: proposal.admin_only })
    }
    else
      form.setFieldsValue({ name: '' })
  };

  const handeApplyTemplate = (id) => {
    if (currentDocument.type === 'template') {
      updateUrl({ tid: id });
      applyTemplate(id);
      handleOk();
    } else if (currentProposal?.id) {
      applyTemplate(id);
      handleOk();
      message.success('Contenu modifié, mais non sauvegardé');
    } else {
      // Loader plein écran pendant la création (expansion des sections liées =
      // fetch templates + tables admin), comme un chargement de document.
      startLoading();
      Promise.resolve(applyTemplateAndSaveProposal(id)).finally(() => {
        stopLoading();
      });
      handleOk();
    }
  };

  const handleSecondModalOk = () => {
    const values = form.getFieldsValue()
    if (modalSelection === 'updateTemplateInfos') {
      updateTemplateInfos(selectedTemplate.id, { name: values.name, document_type_ids: values.document_type_ids || [], admin_only: values.admin_only }).then((e) => {
        updateTemplateInfosStore(e, currentTab);
      });
    } else if (modalSelection === 'duplicateTemplate') {
      duplicateTemplate(selectedTemplate.id, values.name).then(
        (duplicated) => {
          if (!duplicated) return;
          duplicateTemplateStore(duplicated);
          // Ouvre la copie en édition de modèles. Depuis une proposition,
          // « ouvrir » un modèle revient à l'appliquer : on ne remplace pas son
          // contenu sans le demander.
          if (currentDocument.type === 'template') handeApplyTemplate(duplicated.id);
        }
      );
    }
    setIsOpen(false);
  };
  const handleSecondModalCancel = () => {
    setIsOpen(false);
  };
  const normalizeSortValue = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  const compareTextValues = (valueA, valueB) => normalizeSortValue(valueA)
    .localeCompare(normalizeSortValue(valueB), 'fr');

  const getDocumentTypeNames = (record) => documentTypes
    ?.filter((dT) => record.document_type_ids?.includes(dT.id))
    .map((dT) => dT.name)
    .join(', ') || '';

  const cols = (documentType = 'template') => {
    const columns = [
      {
        title: 'Nom',
        key: 'name',
        className: 'nameColumn',
        width: '40%',
        onHeaderCell: () => ({
          style: {
            backgroundColor: '#0B2239',
            color: 'white',
          },
        }),
        onCell: (record) => {
          return {
            style: {
              wordWrap: 'anywhere',
              wordBreak: 'normal',
            },
            onClick: () => {
              handeApplyTemplate(record.id);
            },
          };
        },
        filteredValue: [nameSearch],
        sorter: (recordA, recordB) => compareTextValues(t(recordA.name), t(recordB.name)),
        onFilter: (value, record) => {
          if (!value || !value.trim()) return true;
          if (!record || !record.name) return false;

          // Normaliser les espaces multiples et trim
          const normalizeSpaces = (str) => String(str).replace(/\s+/g, ' ').trim();

          const searchValue = normalizeSpaces(value)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();

          const recordName = normalizeSpaces(record.name)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();

          // Comparer avec le nom original
          if (recordName.includes(searchValue)) return true;

          // Comparer aussi avec le texte traduit si différent
          try {
            const translatedName = t(record.name);
            if (translatedName && translatedName !== record.name) {
              const translatedNameNormalized = normalizeSpaces(translatedName)
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase();
              if (translatedNameNormalized.includes(searchValue)) return true;
            }
          } catch (e) {
            // Ignorer les erreurs de traduction
          }

          return false;
        },
        render: (record) => (
          <TemplateNameColumn record={record} />
        ),
      },
      {
        title: 'Format',
        key: 'format',
        align: 'center',
        className: 'nameColumn',
        sorter: (recordA, recordB) => {
          if (recordA.landscape === recordB.landscape) return 0;
          return recordA.landscape ? 1 : -1;
        },
        onHeaderCell: () => ({
          style: {
            backgroundColor: '#0B2239',
            color: 'white',
            borderLeft: '1px solid white',
          },
        }),
        render: (record) => (
          <p style={{ textAlign: 'center', margin: 0 }}>
            <Tooltip title={documentFormatLabel(record.landscape)} mouseLeaveDelay={0}>
              <FileOutlined
                style={{
                  ...documentFormatIconStyle(record.landscape),
                  margin: 0,
                  marginRight: 8,
                }}
              />
            </Tooltip>
          </p>
        ),
      },
    ];
    if (documentType === 'template') {
      columns.push({
        title: 'Type',
        key: 'templatable',
        align: 'center',
        sorter: (recordA, recordB) => compareTextValues(t(recordA.templatable), t(recordB.templatable)),
        onHeaderCell: () => ({
          style: {
            backgroundColor: '#0B2239',
            color: 'white',
            borderLeft: '1px solid white',
          },
        }),
        render: (record) => (
          <p style={{ textAlign: 'center', margin: 0 }}>
            {t(record.templatable)}
          </p>
        ),
      });
    }
    // if (user?.works)
    columns.push({
      title: 'Métier',
      key: 'work',
      align: 'center',
      sorter: (recordA, recordB) => compareTextValues(t(recordA.work), t(recordB.work)),
      onHeaderCell: () => ({
        style: {
          backgroundColor: '#0B2239',
          color: 'white',
          borderLeft: '1px solid white',
        },
      }),
      render: (record) => (
        <p style={{ textAlign: 'center', margin: 0 }}>{t(record.work)}</p>
      ),
    });
    columns.push({
      title: 'Tags',
      key: 'tags',
      align: 'center',
      sorter: (recordA, recordB) => compareTextValues(getDocumentTypeNames(recordA), getDocumentTypeNames(recordB)),
      onHeaderCell: () => ({
        style: {
          backgroundColor: '#0B2239',
          color: 'white',
          borderLeft: '1px solid white',
        },
      }),
      render: (record) => (
        <p style={{ textAlign: 'center', margin: 0 }}>
          {getDocumentTypeNames(record)}
        </p>
      ),
    });
    // columns.push({
    //   title: 'Aperçu',
    //   key: 'preview',
    //   align: 'center',
    //   onHeaderCell: () => ({
    //     style: {
    //       backgroundColor: '#0B2239',
    //       color: 'white',
    //       borderLeft: '1px solid white',
    //     },
    //   }),
    //   render: (record) => (
    //     <div style={{ display: 'flex', justifyContent: 'center' }}>
    //       {record.preview ? (
    //         <div style={{ width: '40%' }}>
    //           <ModalImage
    //             small={record.preview}
    //             large={record.preview}
    //             alt='Aperçu'
    //           />
    //         </div>
    //       ) : (
    //         <p style={{ textAlign: 'center' }}>
    //           Aperçu non disponible
    //         </p>
    //       )}
    //     </div>
    //   ),
    // });
    columns.push({
      title: 'Actions',
      key: 'action',
      align: 'center',
      width: '10%',
      onHeaderCell: () => ({
        style: {
          backgroundColor: '#0B2239',
          color: 'white',
          borderLeft: '1px solid white',
        },
      }),
      render: (text, record) => {
        const isHidden = record.isHidden || false;
        return (
          <div
            style={{
              justifyContent: 'space-evenly',
              fontSize: '1.7em',
              display: 'flex',
            }}
          >
            {/* <TemplateActions record={record} deleteTemplate={deleteTemplate} /> */}
            {isHidden ? (
              <Tooltip title="Afficher l'élément" mouseLeaveDelay={0}>
                <EyeOutlined
                  style={{ color: 'orange', cursor: 'pointer' }}
                  onClick={() => {
                    restoreTemplate(record.id);
                  }}
                />
              </Tooltip>
            ) : (
              <>
                <Tooltip title='Supprimer' mouseLeaveDelay={0}>
                  <Popconfirm
                    title={'Supprimer ce document ?'}
                    onConfirm={() => {
                      deleteTemplate(record.id);
                    }}
                    okText={t('yes')}
                    cancelText={t('cancel')}
                  >
                    <DeleteOutlined style={{ color: 'red' }} />
                  </Popconfirm>
                </Tooltip>
                <Tooltip title='Renommer' mouseLeaveDelay={0}>
                  <EditOutlined
                    onClick={() => {
                      showModal('updateTemplateInfos', record);
                    }}
                  />
                </Tooltip>
                <Tooltip title='Dupliquer' mouseLeaveDelay={0}>
                  <div
                    style={{
                      transform: 'rotate(180deg)',
                      fontSize: '1em',
                      marginTop: '-3%',
                    }}
                  >
                    <CopyOutlined
                      style={{ transform: 'scaleX(-1)' }}
                      onClick={() => {
                        showModal('duplicateTemplate', record);
                      }}
                    />
                  </div>
                </Tooltip>
              </>
            )
            }
          </div >
        );
      },
    });
    return columns;
  };

  const columns = cols(currentDocument.type).map((c) => {
    if (!c.editable) return c;

    return {
      ...c,
      onCell: (record) => ({
        record,
        editable: c.editable,
        dataIndex: c.dataIndex,
        title: c.title,
      }),
    };
  });
  const handleTabChange = (e) => {
    if (e === 'templates') { setCurrentTab(false); setIsRecursiveTab(false); }
    else if (e === 'sections') { setCurrentTab(true); setIsRecursiveTab(false); }
    else if (e === 'recursive_sections') { setCurrentTab(true); setIsRecursiveTab(true); }
  };

  // Fonctions pour obtenir les données selon l'état du switch
  const getDisplayTemplates = () => {
    if (showHidden) {
      return hiddenTemplates.map(template => ({ ...template, isHidden: true }));
    }
    return templates;
  };

  const getDisplaySections = () => {
    if (showHidden) {
      return hiddenSections.map(section => ({ ...section, isHidden: true }));
    }
    return sections;
  };

  return (
    <Modal
      className='template-modal-fixed-height'
      style={{ textAlign: 'center', minWidth: '1000px' }}
      closable={!forbidClose}
      maskClosable={!forbidClose}
      title={
        forbidClose ? t('Choisissez un template ci dessous') : t('documents')
      }
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      cancelText={t('close')}
      okText={t('ok')}
      width='80vw'
      okButtonProps={{ disabled: forbidClose }}
      cancelButtonProps={{ disabled: forbidClose }}
    >
      {/* Wrapper flex : même structure que libraryApp.jsx (iframe), où la vue
          Dossiers tient dans sa hauteur disponible sans déborder. Sans lui,
          DocumentsFolderView (height:100%) et le Segmented ci-dessous sont deux
          enfants directs en flux normal de .ant-modal-body (pas un conteneur
          flex) : leurs hauteurs s'additionnent et dépassent la modale, cachant
          les dernières lignes du tableau derrière l'overflow:hidden du body. */}
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Sélecteur Documents / Site web : les templates de site web vivaient dans
            la barre d'onglets de l'ancienne vue Liste, qui disparaît. Affiché aux
            seuls porteurs du droit web editor ; pour tout le monde d'autre la modale
            n'a qu'une vue (Dossiers) et n'affiche donc aucun sélecteur. */}
        {canUseWebEditor() && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 16 }}>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { label: 'Documents', value: 'folders', title: '' },
                { label: '🌐 Site web', value: 'web', title: '' },
              ]}
            />
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {viewMode === 'folders' && (
            <DocumentsFolderView
              onApply={handeApplyTemplate}
              onRename={(record) => showModal('updateTemplateInfos', record)}
              onDuplicate={(record) => showModal('duplicateTemplate', record)}
              onDelete={(id) => deleteTemplate(id)}
              onNewTemplate={
                isAdmin && currentDocument.type === 'template'
                  ? (docFilter) => {
                      const { isSection, isRecursive } = newTemplateDefaults(docFilter);
                      if (isSection !== undefined) changeCurrentTab(isSection);
                      newTemplateOnClick(isRecursive);
                    }
                  : undefined
              }
              showHidden={showHidden}
              onShowHiddenChange={
                isAdmin && user?.role === 'superadmin' ? setShowHidden : undefined
              }
              hiddenTemplates={hiddenTemplates}
              hiddenSections={hiddenSections}
              onRestore={restoreTemplate}
            />
          )}
          {viewMode === 'web' && <WebTemplatesPanel />}
        </div>
      </div>
      {viewMode === 'list' && (
        <>
      {isAdmin && currentDocument.type === 'template' && (
        <div style={{ paddingBottom: '20px' }}>
          <Button
            style={{ marginTop: 10, marginLeft: 0 }}
            onClick={() => {
              newTemplateOnClick(isRecursiveTab);
              changeCurrentTab(currentTab);
            }}
          >
            {t('new-template')}
          </Button>
        </div>
      )}
      {isAdmin && user?.role === 'superadmin' && (
        <div style={{ paddingBottom: '20px', textAlign: 'center' }}>
          <Switch
            checked={showHidden}
            onChange={setShowHidden}
            checkedChildren="Cachés"
            unCheckedChildren="Visibles"
          />
          <span style={{ marginLeft: '10px' }}>
            {showHidden ? 'Afficher les éléments cachés' : 'Afficher les éléments visibles'}
          </span>
        </div>
      )}
      <Tabs
        defaultActiveKey='1'
        centered
        onTabClick={(e) => {
          handleTabChange(e);
        }}
        /* Administration générale : règles transverses (publication d'un site
           web aujourd'hui, autres domaines plus tard). Onglet séparé pour ne
           pas mélanger le contenu et les règles. */
        tabBarExtraContent={canAdministrateGeneralRules() ? (
          <Tooltip title='Règles générales de la société (administration)'>
            <Button
              size='small'
              icon={<SettingOutlined />}
              onClick={() => window.open('/admin', '_blank')}
            >
              Règles générales
            </Button>
          </Tooltip>
        ) : null}
      >
        <Tabs.TabPane tab='Modèles' key='templates'>
          <div
            style={{
              display: 'flex',
              width: '70%',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '1%',
            }}
          >
            <div style={{ width: '25%' }}>Recherche par nom:</div>
            <Search
              key='nameSearch'
              placeholder='Rechercher un modèle par nom'
              value={nameSearch}
              style={{
                width: '50%',
                marginBottom: '2%',
                marginTop: '2%',
                marginLeft: '2%',
              }}
              onChange={(e) => {
                setNameSearch(e.target.value);
              }}
            />
          </div>
          <Table
            className='template-modal-table'
            showSorterTooltip={false}
            pagination={false}
            scroll={{ y: 'calc(80vh - 390px)' }}
            columns={columns}
            dataSource={getDisplayTemplates()}
            rowKey={(record) => record.id}
          />
        </Tabs.TabPane>
        {currentDocument.type === 'template' ? (
          <Tabs.TabPane tab='Sections' key='sections'>
            <div
              style={{
                display: 'flex',
                width: '70%',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '1%',
              }}
            >
              <div style={{ width: '25%' }}>Recherche par nom:</div>
              <Search
                key='nameSearch'
                placeholder='Rechercher un modèle par nom'
                value={nameSearch}
                style={{
                  width: '50%',
                  marginBottom: '2%',
                  marginTop: '2%',
                  marginLeft: '2%',
                }}
                onChange={(e) => {
                  setNameSearch(e.target.value);
                }}
              />
            </div>
            <Table
              className='template-modal-table'
              showSorterTooltip={false}
              pagination={false}
              scroll={{ y: 'calc(80vh - 390px)' }}
              columns={columns}
              dataSource={getDisplaySections().filter(s => !s.isRecursive)}
              rowKey={(record) => record.id}
            />
          </Tabs.TabPane>
        ) : null}
        {currentDocument.type === 'template' ? (
          <Tabs.TabPane tab='Sections récursives' key='recursive_sections'>
            <div
              style={{
                display: 'flex',
                width: '70%',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '1%',
              }}
            >
              <div style={{ width: '25%' }}>Recherche par nom:</div>
              <Search
                key='nameSearchRecursive'
                placeholder='Rechercher un modèle par nom'
                value={nameSearch}
                style={{
                  width: '50%',
                  marginBottom: '2%',
                  marginTop: '2%',
                  marginLeft: '2%',
                }}
                onChange={(e) => {
                  setNameSearch(e.target.value);
                }}
              />
            </div>
            <Table
              className='template-modal-table'
              showSorterTooltip={false}
              pagination={false}
              scroll={{ y: 'calc(80vh - 390px)' }}
              columns={columns}
              dataSource={getDisplaySections().filter(s => s.isRecursive)}
              rowKey={(record) => record.id}
            />
          </Tabs.TabPane>
        ) : null}

      </Tabs>
        </>
      )}

      <Modal
        title={
          modalSelection === 'updateTemplateInfos'
            ? 'Edition du document'
            : modalSelection === 'createTemplate'
              ? 'Création du document'
              : modalSelection === 'duplicateTemplate'
                ? 'Duplication du document'
                : null
        }
        open={isOpen}
        onOk={handleSecondModalOk}
        onCancel={handleSecondModalCancel}
        cancelText={t('cancel')}
        width='50%'
        destroyOnClose
      >
        {modalSelection === 'updateTemplateInfos' ? (
          <Form
            form={form}
            layout='horizontal'
            labelCol={{
              flex: "150px",
            }}
            labelAlign='left'
            labelWrap
            style={{
              marginTop: 16
            }}
          >
            <Form.Item
              label="Nouveau nom"
              name="name"
            >
              <Input
                key='renameInput'
                placeholder='Nouveau nom'
                size='middle'
              />
            </Form.Item>
            <Form.Item
              label="Tags"
              name="document_type_ids"
            >
              <DocumentTypesSelector />
            </Form.Item>
            <AdminOnlyFormItem />
          </Form>
        ) : modalSelection === 'duplicateTemplate' ? (
          <Form
            form={form}
            layout='horizontal'
            labelAlign='left'
            style={{
              marginTop: 16
            }}
          >
            <div style={{ marginBottom: '3%', display: 'flex' }}>
              Document dupliqué :
              <div style={{ fontWeight: 'bold', marginLeft: '1%' }}>
                {selectedTemplate.name}
              </div>
            </div>
            <Form.Item
              label="Nom du template dupliqué"
              name="name"
            >
              <Input
                key='renameInput'
                placeholder='Nouveau nom du modèle dupliqué'
                size='middle'
              />
            </Form.Item>
          </Form>
        ) : null}
      </Modal>
    </Modal >
  );
};

export default TemplateModal;
